import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import logoTransparent from '../../assets/logo_transparent.png';

export default function PrivacyPolicy() {
    const navigate = useNavigate();

    const handleBack = () => {
        navigate('/', { replace: true });
    };

    return (
        <div className="min-h-screen bg-[#2b0063] py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto bg-white shadow-2xl rounded-2xl overflow-hidden">
                {/* Header with Logo and Back Button */}
                <div className="absolute top-4 left-4 flex items-center space-x-4">
                    <button 
                        onClick={handleBack} 
                        className="flex items-center bg-transparent hover:bg-white/10 p-2 rounded-full transition-colors"
                    >
                        <ArrowLeft className="w-6 h-6 text-white mr-2" />
                        <span className="text-white font-semibold">Back</span>
                    </button>
                    
                </div>

                <div className="p-8 sm:p-12">
                    <div className="flex justify-between items-center mb-8 border-b-2 border-purple-200 pb-2">
                        <h1 className="text-4xl font-bold text-purple-900">
                            Privacy Policy
                        </h1>
                        <img 
                            src={logoTransparent} 
                            alt="LastCall Logo" 
                            className="h-20 w-auto"
                        />
                    </div>

                    <p className="text-sm text-gray-600 mb-8 italic">
                        Last updated: February 9th, 2025
                    </p>

                    <div className="prose prose-lg text-gray-800 space-y-8">
                        <section>
                            <h2 className="text-2xl font-semibold mb-4 text-purple-800">1. Company Contact Information</h2>
                            <p>If you have any questions or concerns about this Privacy Policy or your personal information, please contact us at:</p>
                            <ul className="list-disc pl-6 mt-2">
                                <li><strong>Company Name</strong>: LastCall <em>(Go Disgo LLC)</em></li>
                                <li><strong>Contact Email</strong>: <a href="mailto:support@lastcallforbars.com" className="text-purple-600 hover:underline">support@lastcallforbars.com</a></li>
                                <li><strong>Phone Number</strong>: (202) 494-4716</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold mb-4 text-purple-800">2. Personal Information We Collect</h2>
                            <p>We collect the following types of personal information, and depending on the location, there may be additional requested information as well:</p>
                            <ul className="list-disc pl-6 mt-2">
                                <li><strong>Contact Information</strong>: Name, phone number, email address, age, and gender.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold mb-4 text-purple-800">3. Why We Collect Personal Information</h2>
                            <p>We collect personal information to:</p>
                            <ul className="list-disc pl-6 mt-2">
                                <li>Facilitate SMS-based communication between bars/venues and customers about events, promotions, and deals.</li>
                                <li>Analyze and improve our services to better meet user needs.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold mb-4 text-purple-800">4. How We Use Your Personal Information</h2>
                            <p>The personal information we collect is used for the following purposes:</p>
                            <ul className="list-disc pl-6 mt-2">
                                <li><strong>Service Delivery</strong>: To send SMS notifications from the bars/venues you've opted in to.</li>
                                <li><strong>Personalization</strong>: To tailor the content you see based on your interactions with bars/venues.</li>
                                <li><strong>Analytics</strong>: To assess trends, manage the platform, and improve our service.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold mb-4 text-purple-800">5. Sharing and Selling Personal Information</h2>
                            <p>We do <strong>not</strong> sell or share your personal information with third parties for promotional purposes. Your information gathered during the SMS opt-in process will only be shared with the bar/venue you've signed up for communication from.</p>
                        </section>

                        {/* Continue with remaining sections using similar styling */}
                        <section>
                            <h2 className="text-2xl font-semibold mb-4 text-purple-800">6. International Data Transfers</h2>
                            <p>If your data is transferred outside of your country of residence, we take steps to ensure it is adequately protected in compliance with applicable data protection laws.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold mb-4 text-purple-800">7. Your Data Rights</h2>
                            <p>As a user, you have the following rights regarding your personal information:</p>
                            <ul className="list-disc pl-6 mt-2">
                                <li><strong>Access</strong>: You can request a copy of the personal data we hold about you.</li>
                                <li><strong>Correction</strong>: You can request corrections to any inaccurate or incomplete data.</li>
                                <li><strong>Deletion</strong>: You can request the deletion of your personal data.</li>
                                <li><strong>Withdrawal of Consent</strong>: You can withdraw your consent to receive SMS messages or other services at any time.</li>
                                <li><strong>Data Portability</strong>: You can request your data in a machine-readable format.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold mb-4 text-purple-800">8. Exercising Your Rights</h2>
                            <p>To exercise any of the rights listed above, please contact us at <a href="mailto:support@lastcallforbars.com" className="text-purple-600 hover:underline">support@lastcallforbars.com</a>. We will respond within a reasonable timeframe, typically within 30 days.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold mb-4 text-purple-800">9. Data Retention</h2>
                            <p>We retain your personal data for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.</p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-semibold mb-4 text-purple-800">10. Updates to This Privacy Policy</h2>
                            <p>We may update this Privacy Policy from time to time. Any changes will be posted on this page with the date of the latest revision. Please check back periodically to stay informed of any updates.</p>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
}
